import type { SxProps, Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { brandPrimary, brandPrimaryHover, brandPrimaryMuted } from "./tokens";

/** Primary brand color for inputs, selects, loaders (aligned with Tailwind `primary`). */
export const formPrimary = brandPrimary;
export const formPrimaryHover = brandPrimaryHover;
export const formPrimaryMuted = brandPrimaryMuted;

/** Shared control height (dropdowns + text inputs). */
export const formControlHeight = 48;

/** Shared corner radius — employer portal inputs & dropdowns (8px). */
export const formFieldBorderRadius = 8;
export const formFieldBorderRadiusPx = `${formFieldBorderRadius}px`;

/** Auth forms on short viewports — matches `.auth-form-compact` in index.css */
export const AUTH_FORM_COMPACT_CONTROL_HEIGHT = 42;

/** Menu item states — used by MUI theme + Select styles. */
export const formMenuItem = {
	hoverBg: "rgba(2, 108, 181, 0.1)",
	hoverColor: "#1e293b",
	selectedBg: brandPrimary,
	selectedColor: "#ffffff",
	selectedHoverBg: brandPrimaryHover,
} as const;

const borderError = "#d32f2f";

/** Placeholder tone for inputs and empty selects — keep lighter than selected text (#111827). */
export const formFieldPlaceholderColor = "#9CA3AF";
export const formFieldSelectedColor = "#111827";
/** Disabled/read-only filled values — same darkness as editable text for readability. */
export const formFieldDisabledTextColor = formFieldSelectedColor;
export const formSelectPlaceholderClassName = "psda-select-placeholder";
export const formSelectValueClassName = "psda-select-value";

const placeholderColor = formFieldPlaceholderColor;

/** Shared field surface — text inputs and dropdowns (Figma). */
export const formFieldBg = "#FFF";
/** Disabled/read-only field background — visibly distinct from editable white fields. */
export const formFieldDisabledBg = "#E5E7EB";
export const formFieldBorder = "#D9D9D9";
export const formFieldBorderWidth = 1.2;
const fieldBorderHover = "#C4C4C4";

/** @deprecated Use `formFieldBg`. */
export const selectFieldBg = formFieldBg;
/** @deprecated Use `formFieldBg`. */
export const inputFieldBg = formFieldBg;
/** @deprecated Use `formFieldBorder`. */
export const selectFieldBorder = formFieldBorder;

/** Figma chevron tile for standard 48px controls (Frame — 35×36, 4px radius). */
export const selectChevron = {
	width: 35,
	height: 36,
	borderRadius: 4,
	rightInset: 5,
} as const;

/** Smaller chevron for compact selects (e.g. Year dropdown on dashboard cards). */
export const selectChevronCompact = {
	width: 30,
	height: 30,
	borderRadius: 4,
	rightInset: 4,
} as const;

export interface SelectChevronSpec {
	width: number;
	height: number;
	borderRadius: number;
	rightInset: number;
}

export const getSelectChevron = (controlHeight = formControlHeight): SelectChevronSpec =>
	controlHeight <= 44 ? selectChevronCompact : selectChevron;

export const getChevronReserve = (controlHeight = formControlHeight): number => {
	const chevron = getSelectChevron(controlHeight);
	return chevron.width + chevron.rightInset + 8;
};

/** Tailwind classes for the chevron button — keep in sync with `selectChevron` above. */
export const selectChevronCapClassName =
	"absolute z-[2] flex cursor-pointer items-center justify-center border-0 bg-primary p-0 outline-none focus-visible:ring-2 focus-visible:ring-[#026CB5]/40";

/** Muted chevron when the select is disabled — matches disabled text inputs. */
export const selectChevronCapDisabledClassName =
	"absolute z-[2] flex cursor-not-allowed items-center justify-center border-0 bg-[#E5E7EB] p-0 outline-none";

export const selectChevronDisabledIconColor = "#9CA3AF";

const fieldBorderSx = (error?: boolean): SystemStyleObject<Theme> => ({
	borderColor: error ? borderError : formFieldBorder,
	borderWidth: error ? 1.2 : formFieldBorderWidth,
	borderRadius: formFieldBorderRadiusPx,
});

const outlinedFieldBorderStates = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": fieldBorderSx(error),
	"& .MuiOutlinedInput-notchedOutline": fieldBorderSx(error),
	"&:hover fieldset": {
		borderColor: error ? borderError : fieldBorderHover,
	},
	"&:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: error ? borderError : fieldBorderHover,
	},
	"&.Mui-focused fieldset": fieldBorderSx(error),
	"&.Mui-focused .MuiOutlinedInput-notchedOutline": fieldBorderSx(error),
	"&.Mui-error fieldset": {
		borderColor: `${borderError} !important`,
	},
	"&.Mui-error .MuiOutlinedInput-notchedOutline": {
		borderColor: `${borderError} !important`,
	},
});

/** Outlined field shell — shared by text inputs and selects. */
export const formOutlinedFieldShellSx = (error?: boolean): SystemStyleObject<Theme> => ({
	backgroundColor: `${formFieldBg} !important`,
	borderRadius: formFieldBorderRadiusPx,
	overflow: "hidden",
	...outlinedFieldBorderStates(error),
});

/** Use on OutlinedInput when the field label is rendered outside (SelectWithSearch, AppSelect, etc.). */
export const outlinedInputExternalLabelProps = {
	notched: false,
} as const;

const selectOutlineNoNotchSx = (error?: boolean): SystemStyleObject<Theme> => ({
	"& fieldset": {
		top: 0,
		...fieldBorderSx(error),
	},
	"& .MuiOutlinedInput-notchedOutline": {
		top: 0,
		...fieldBorderSx(error),
		"& legend": {
			display: "none",
			maxWidth: 0.01,
		},
	},
});

/** Select / dropdown outlined root. */
export const selectOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error),
	...selectOutlineNoNotchSx(error),
	minHeight: height,
	height,
	paddingRight: `${getChevronReserve(height)}px !important`,
	overflow: "visible",
	cursor: "pointer",
});

/** Text outlined root with fixed height. */
export const textOutlinedInputSx = (error?: boolean, height = formControlHeight): SystemStyleObject<Theme> => ({
	...formOutlinedFieldShellSx(error),
	minHeight: height,
	height,
});

const selectInnerSx = (height = formControlHeight): SystemStyleObject<Theme> => ({
	padding: height <= 40 ? "8px 8px 8px 12px" : "13px 8px 13px 15px",
	fontSize: height <= 40 ? "13px" : "14px",
	fontWeight: 400,
	lineHeight: 1.5,
	display: "flex",
	alignItems: "center",
	fontFamily: "Poppins, sans-serif",
	minHeight: height,
	height,
	boxSizing: "border-box",
	color: formFieldPlaceholderColor,
	backgroundColor: "transparent",
	cursor: "pointer",
});

/**
 * Unified dropdown field chrome — AppSelect, SelectWithSearch, InputField `select`.
 */
export const selectControlSx = (error?: boolean, height = formControlHeight): SxProps<Theme> => {
	const outlined = selectOutlinedInputSx(error, height);
	return {
		"&.MuiOutlinedInput-root": outlined,
		"& .MuiOutlinedInput-root": outlined,
		"& .MuiSelect-select": selectInnerSx(height),
		"& .MuiSelect-icon": {
			display: "none",
		},
		"&.MuiOutlinedInput-root.Mui-disabled": {
			backgroundColor: `${formFieldDisabledBg} !important`,
			opacity: 1,
			cursor: "not-allowed",
			"&:hover fieldset": {
				borderColor: `${formFieldBorder} !important`,
			},
			"&:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: `${formFieldBorder} !important`,
			},
		},
		"& .MuiOutlinedInput-root.Mui-disabled": {
			backgroundColor: `${formFieldDisabledBg} !important`,
			opacity: 1,
			cursor: "not-allowed",
			"&:hover fieldset": {
				borderColor: `${formFieldBorder} !important`,
			},
			"&:hover .MuiOutlinedInput-notchedOutline": {
				borderColor: `${formFieldBorder} !important`,
			},
		},
		"& .MuiSelect-select.Mui-disabled": {
			WebkitTextFillColor: formFieldDisabledTextColor,
			color: formFieldDisabledTextColor,
			opacity: 1,
			cursor: "not-allowed",
		},
		"& .MuiSelect-select.Mui-disabled span": {
			color: `${formFieldDisabledTextColor} !important`,
			WebkitTextFillColor: `${formFieldDisabledTextColor} !important`,
		},
		[`& .MuiSelect-select.Mui-disabled .${formSelectPlaceholderClassName}`]: {
			color: `${formFieldPlaceholderColor} !important`,
			WebkitTextFillColor: `${formFieldPlaceholderColor} !important`,
		},
		[`& .MuiSelect-select .${formSelectPlaceholderClassName}`]: {
			color: `${formFieldPlaceholderColor} !important`,
			WebkitTextFillColor: `${formFieldPlaceholderColor} !important`,
		},
		[`& .MuiSelect-select .${formSelectValueClassName}`]: {
			color: `${formFieldSelectedColor} !important`,
			WebkitTextFillColor: `${formFieldSelectedColor} !important`,
		},
		[`& .MuiSelect-select.Mui-disabled .${formSelectValueClassName}`]: {
			color: `${formFieldDisabledTextColor} !important`,
			WebkitTextFillColor: `${formFieldDisabledTextColor} !important`,
		},
	};
};

/** Compact year selector on dashboard cards — same chrome as AppSelect, smaller height + chevron. */
export const yearSelectControlHeight = 40;

export const yearSelectControlSx = (error?: boolean): SxProps<Theme> =>
	selectControlSx(error, yearSelectControlHeight);

const textInputInnerBase: SystemStyleObject<Theme> = {
	fontFamily: "Poppins, sans-serif",
	fontSize: "14px",
	fontWeight: 400,
	color: "#111827",
	boxSizing: "border-box",
	backgroundColor: "transparent",
};

export type TextFieldControlSize = number | { height?: string; padding?: string };

/** Align plain text inputs with SelectWithSearch (`paddingLeft: 12px`). */
export const textInputPaddingDefault = "13px 8px 13px 12px";

/** Icon inset for adorned text inputs — matches MUI OutlinedInput adornedStart on selects. */
export const textInputAdornedRootPaddingLeft = "14px";

/** Gap between start icon and text — mirrors select `paddingLeft` after adornment. */
export const textInputAdornedTextPaddingLeft = "8px";

const textInputAdornedSx = (): SystemStyleObject<Theme> => ({
	"& .MuiOutlinedInput-root.MuiInputBase-adornedStart": {
		paddingLeft: textInputAdornedRootPaddingLeft,
	},
	"& .MuiOutlinedInput-root.MuiInputBase-adornedStart input, & .MuiOutlinedInput-root.MuiInputBase-adornedStart textarea":
		{
			paddingLeft: textInputAdornedTextPaddingLeft,
		},
});

/** Normalize legacy input padding so text fields match dropdown left inset. */
export const resolveTextInputPadding = (padding?: string): string => {
	if (!padding || padding === "13px 15px" || padding === "16px 20px") {
		return textInputPaddingDefault;
	}
	return padding;
};

/** Legacy auth sizes used `height: "10px"` — always use shared control height unless explicitly tall (e.g. 80px). */
const resolveCustomFieldHeight = (height?: string): number => {
	if (!height) return formControlHeight;
	const parsed = parseInt(String(height), 10);
	if (!Number.isFinite(parsed) || parsed < 32) return formControlHeight;
	return parsed;
};

/** Resolve control height from InputField size presets or custom size objects. */
export const resolveInputFieldControlHeight = (
	size?: TextFieldControlSize | "small" | "medium" | "large",
): number => {
	if (typeof size === "number") return size;
	if (typeof size === "object" && size !== null) {
		return resolveCustomFieldHeight(size.height);
	}
	return formControlHeight;
};

/**
 * Unified TextField chrome — all InputField instances (portal + auth).
 */
export const textFieldControlSx = (error?: boolean, size?: TextFieldControlSize): SxProps<Theme> => {
	if (typeof size === "object" && size !== null) {
		const height = resolveCustomFieldHeight(size.height);
		const padding = resolveTextInputPadding(size.padding);
		return {
			"& .MuiOutlinedInput-root": textOutlinedInputSx(error, height),
			...textInputAdornedSx(),
			"& input, & textarea": {
				...textInputInnerBase,
				padding,
				height,
				minHeight: height,
			},
			"& input::placeholder, & textarea::placeholder": {
				color: placeholderColor,
				opacity: 1,
			},
			"& input[type=number]": {
				MozAppearance: "textfield",
			},
			"& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
				WebkitAppearance: "none",
				margin: 0,
			},
		};
	}

	const height = typeof size === "number" ? size : formControlHeight;

	return {
		"& .MuiOutlinedInput-root": textOutlinedInputSx(error, height),
		...textInputAdornedSx(),
		"& input, & textarea": {
			...textInputInnerBase,
			padding: textInputPaddingDefault,
			height,
		},
		"& input::placeholder, & textarea::placeholder": {
			color: placeholderColor,
			opacity: 1,
		},
		"& input[type=number]": {
			MozAppearance: "textfield",
		},
		"& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
			WebkitAppearance: "none",
			margin: 0,
		},
	};
};

/** Outlined TextField border (legacy helper). */
export const getOutlinedFieldBorderColor = (error?: boolean) => (error ? borderError : formPrimary);

/** @deprecated Alias — same as `selectControlSx`. */
export const selectFieldDashboardSx = selectControlSx;

export const mergeSelectSx = (
	error?: boolean,
	...overrides: (SxProps<Theme> | undefined)[]
): SxProps<Theme> => [selectControlSx(error), ...overrides.filter(Boolean)] as SxProps<Theme>;

export const defaultSelectMenuProps = {
	PaperProps: {
		sx: {
			borderRadius: 2,
			boxShadow: "0 2px 8px 0 rgba(16, 24, 40, 0.04)",
			mt: 1,
		},
	},
	sx: {
		zIndex: 10001,
	},
} as const;

export { placeholderColor };
